import { Navbar } from '../components/landing/Navbar'
import { HeroCard } from '../components/landing/HeroCard'
import { CONFIG } from '../config/constants'
import { Activity, ArrowRight, BarChart3, Cpu, Lock, Shield, TrendingUp, Zap, Sparkles, Compass, CheckCircle2, XCircle, Gauge, RefreshCw, Layers3, ChevronDown, Terminal, CpuIcon, Database, Quote } from 'lucide-react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useAuth } from '../auth/AuthContext'
import { useState } from 'react'

export const Route = createFileRoute('/')({ component: LandingPage })

function LandingPage() {
    const { isAuthenticated } = useAuth()
    const navigate = useNavigate()
    const [selectedSymbol, setSelectedSymbol] = useState<string>(CONFIG.SYMBOLS[0] || 'NIFTY')
    const [activeTab, setActiveTab] = useState<'chain' | 'signals' | 'crossover' | 'ai'>('chain')
    const [openFaq, setOpenFaq] = useState<number | null>(0)

    const handleAuthAction = () => {
        if (isAuthenticated) {
            navigate({ to: '/home' })
        } else {
            navigate({ to: '/login', search: { redirect: '/' } })
        }
    }

    return (
        <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-100/70 text-slate-900 font-sans selection:bg-[#005451]/15 selection:text-[#005451] overflow-x-hidden transition-colors duration-300">
            {/* --- Ultra-Premium Ambient Light Background Halos --- */}
            <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none transform-gpu opacity-80">
                <div className="absolute top-[-10%] left-[-10%] w-[55vw] h-[55vw] bg-gradient-to-br from-emerald-100/60 via-teal-100/40 to-transparent blur-[140px] rounded-full mix-blend-multiply animate-[pulse_6s_ease-in-out_infinite]" />
                <div className="absolute top-[25%] right-[-10%] w-[45vw] h-[45vw] bg-gradient-to-bl from-indigo-100/50 via-purple-100/30 to-transparent blur-[140px] rounded-full mix-blend-multiply" />
                <div className="absolute bottom-[-10%] left-[20%] w-[60vw] h-[45vw] bg-gradient-to-tr from-cyan-100/50 via-emerald-100/30 to-transparent blur-[150px] rounded-full mix-blend-multiply" />
            </div>

            {/* --- Subtle Crisp Light Grid Overlay --- */}
            <div className="fixed inset-0 z-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHBhdGggZD0iTSAwIDQwIEwgNDAgNDAgTSA0MCAwIEwgNDAgNDAiIGZpbGw9Im5vbmUiIHN0cm9rZT0icmdiYSgxNTYsMTYzLDE3NSwwLjA4KSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9zdmc+')] pointer-events-none" />

            {/* --- Navbar --- */}
            <div className="relative z-50">
                <Navbar />
            </div>

            {/* --- Hero Section --- */}
            <section className="relative z-10 pt-32 pb-20 lg:pt-44 lg:pb-32 px-6">
                <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 lg:gap-16 items-center min-h-[75vh]">
                    
                    {/* Text Content */}
                    <div className="space-y-8 text-center lg:text-left">
                        
                        {/* Index Switcher Ribbon */}
                        <div className="flex flex-wrap items-center justify-center lg:justify-start gap-2 animate-fade-in">
                            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white border border-slate-200/80 shadow-sm text-xs font-black uppercase tracking-wider text-slate-600">
                                <span className="relative flex h-2.5 w-2.5">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.8)]"></span>
                                </span>
                                Terminal Feed:
                            </div>
                            {CONFIG.SYMBOLS.map((sym) => (
                                <button
                                    key={sym}
                                    onClick={() => setSelectedSymbol(sym)}
                                    className={`px-3.5 py-1.5 rounded-full text-xs font-black uppercase tracking-wider transition-all duration-300 border ${
                                        selectedSymbol === sym
                                            ? 'bg-gradient-to-r from-[#005451] via-[#006e6a] to-[#005451] text-white border-[#005451] shadow-md shadow-[#005451]/20 scale-105'
                                            : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-100 hover:text-slate-900'
                                    }`}
                                >
                                    {sym}
                                </button>
                            ))}
                        </div>

                        {/* High-Impact Headline */}
                        <h1 className="text-5xl sm:text-7xl lg:text-7xl font-black tracking-tight text-slate-900 leading-[1.05]">
                            Decode Market <br />
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#005451] via-[#007a76] to-[#00a8a1] drop-shadow-xs">
                                Institutional Physics
                            </span>
                        </h1>

                        <p className="text-lg sm:text-xl text-slate-600 max-w-2xl mx-auto lg:mx-0 leading-relaxed font-medium">
                            Real-time options chain decoding, automated support & resistance strikes, gamma volume analytics, and institutional positioning matrix in a unified quantitative terminal.
                        </p>

                        {/* CTA Action Buttons */}
                        <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start pt-2 animate-fade-in">
                            <button
                                onClick={handleAuthAction}
                                className="group relative px-9 py-4.5 bg-gradient-to-r from-[#005451] via-[#006e6a] to-[#005451] hover:from-[#003d3b] hover:to-[#003d3b] text-white rounded-2xl font-bold text-lg shadow-[0_10px_35px_-5px_rgba(0,84,81,0.35)] transition-all duration-300 hover:-translate-y-1 active:scale-95 flex items-center justify-center gap-3 overflow-hidden"
                            >
                                <div className="absolute inset-0 w-1/2 h-full bg-white/25 skew-x-12 -translate-x-full group-hover:translate-x-[300%] transition-transform duration-1000" />
                                <Zap size={22} className="fill-current text-teal-200 group-hover:scale-110 transition-transform" />
                                <span>Initialize Terminal</span>
                                <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
                            </button>

                            <a
                                href="#features"
                                className="px-8 py-4.5 bg-white hover:bg-slate-50 text-slate-900 border border-slate-200/90 rounded-2xl font-bold text-lg shadow-lg shadow-slate-200/50 transition-all duration-300 hover:-translate-y-1 active:scale-95 flex items-center justify-center gap-2.5"
                            >
                                <Compass size={20} className="text-[#005451]" /> Explore Features
                            </a>
                        </div>

                        {/* Trust Badges */}
                        <div className="pt-4 flex flex-wrap items-center justify-center lg:justify-start gap-6 text-slate-500 text-xs font-semibold">
                            <div className="flex items-center gap-1.5">
                                <CheckCircle2 size={16} className="text-emerald-600" /> High Velocity Processing
                            </div>
                            <div className="flex items-center gap-1.5">
                                <CheckCircle2 size={16} className="text-emerald-600" /> Real-time Analytics
                            </div>
                            <div className="flex items-center gap-1.5">
                                <CheckCircle2 size={16} className="text-emerald-600" /> Zero Latency
                            </div>
                        </div>
                    </div>

                    {/* 3D Visual Hero Card */}
                    <div className="relative">
                        <HeroCard symbol={selectedSymbol} />
                    </div>
                </div>
            </section>

            {/* --- Stats Counter Ribbon --- */}
            <section className="relative z-10 py-8 px-6">
                <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6 p-8 rounded-3xl bg-white border border-slate-200/80 shadow-xl shadow-slate-200/40">
                    <div className="space-y-1 text-center md:text-left">
                        <div className="flex items-center justify-center md:justify-start gap-2 text-[#005451]">
                            <Gauge size={22} />
                            <span className="text-3xl font-black font-mono">&lt; 2s</span>
                        </div>
                        <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Live Feed Speed</div>
                    </div>
                    <div className="space-y-1 text-center md:text-left">
                        <div className="flex items-center justify-center md:justify-start gap-2 text-emerald-600">
                            <Shield size={22} />
                            <span className="text-3xl font-black font-mono">99.9%</span>
                        </div>
                        <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Cloud Backup Uptime</div>
                    </div>
                    <div className="space-y-1 text-center md:text-left">
                        <div className="flex items-center justify-center md:justify-start gap-2 text-indigo-600">
                            <Layers3 size={22} />
                            <span className="text-3xl font-black font-mono">3 Indexes</span>
                        </div>
                        <div className="text-xs font-bold uppercase tracking-wider text-slate-500">NIFTY, SENSEX, BANKEX</div>
                    </div>
                    <div className="space-y-1 text-center md:text-left">
                        <div className="flex items-center justify-center md:justify-start gap-2 text-amber-600">
                            <RefreshCw size={22} />
                            <span className="text-3xl font-black font-mono">1-Min</span>
                        </div>
                        <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Intraday Auto Sync</div>
                    </div>
                </div>
            </section>

            {/* --- Interactive Feature Preview (Visual Showcase) --- */}
            <section className="py-20 px-6 relative z-10">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-12 space-y-3">
                        <h2 className="text-3xl sm:text-5xl font-black text-slate-900 tracking-tight">
                            Interactive Terminal Architecture
                        </h2>
                        <p className="text-slate-600 text-base font-medium max-w-xl mx-auto">
                            Switch modules below to inspect our quantitative analytics engine.
                        </p>
                    </div>

                    {/* Tab Navigation */}
                    <div className="flex flex-wrap justify-center gap-3 mb-10">
                        {[
                            { id: 'chain', label: '📊 Real-Time Option Chain' },
                            { id: 'signals', label: '⚡ Strikes Activity Radar' },
                            { id: 'crossover', label: '🚨 Crossover Analysis' },
                            { id: 'ai', label: '🤖 AI Strategy Engine' },
                        ].map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id as any)}
                                className={`px-6 py-3.5 rounded-2xl font-bold text-sm transition-all duration-300 flex items-center gap-2.5 shadow-sm ${
                                    activeTab === tab.id
                                        ? 'bg-gradient-to-r from-[#005451] to-[#007a76] text-white shadow-lg shadow-[#005451]/20 scale-105'
                                        : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-100 hover:text-slate-900'
                                }`}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>

                    {/* Interactive Mockup Container */}
                    <div className="relative rounded-3xl p-6 md:p-8 bg-white border border-slate-200/90 shadow-2xl shadow-slate-200/60 overflow-hidden transition-all duration-500">
                        <div className="absolute top-0 right-0 w-96 h-96 bg-[#005451]/5 blur-3xl rounded-full pointer-events-none" />

                        {activeTab === 'chain' && (
                            <div className="space-y-4 animate-fade-in">
                                <div className="flex items-center justify-between border-b border-slate-200 pb-4">
                                    <div className="flex items-center gap-3">
                                        <div className="w-3 h-3 rounded-full bg-rose-500" />
                                        <div className="w-3 h-3 rounded-full bg-amber-500" />
                                        <div className="w-3 h-3 rounded-full bg-emerald-500" />
                                        <span className="text-xs font-mono text-slate-500 ml-2">Terminal // Real-Time Strategy Chain ({selectedSymbol})</span>
                                    </div>
                                    <span className="text-xs font-bold px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-300">ACTIVE FEED</span>
                                </div>
                                <div className="grid grid-cols-5 gap-3 text-center text-xs font-bold font-mono py-2.5 bg-slate-100 rounded-xl text-slate-600">
                                    <div>CALL OI</div>
                                    <div>CE CHNG</div>
                                    <div className="text-[#005451]">STRIKE PRICE</div>
                                    <div>PE CHNG</div>
                                    <div>PUT OI</div>
                                </div>
                                {[
                                    { ceOi: '1,420,500', ceChg: '+14.2%', strike: '24,400', peChg: '-5.1%', peOi: '850,200', isAtm: false },
                                    { ceOi: '1,890,100', ceChg: '+22.8%', strike: '24,450', peChg: '+2.4%', peOi: '1,120,400', isAtm: false },
                                    { ceOi: '2,450,800', ceChg: '+45.1%', strike: '24,500', peChg: '+48.2%', peOi: '2,680,900', isAtm: true },
                                    { ceOi: '1,150,300', ceChg: '-8.4%', strike: '24,550', peChg: '+18.5%', peOi: '1,940,600', isAtm: false },
                                    { ceOi: '920,400', ceChg: '-12.1%', strike: '24,600', peChg: '+25.9%', peOi: '2,150,800', isAtm: false },
                                ].map((row, i) => (
                                    <div key={i} className={`grid grid-cols-5 gap-3 text-center text-xs font-mono font-bold py-3.5 rounded-xl border transition-all ${
                                        row.isAtm
                                            ? 'bg-[#005451]/10 border-[#005451]/30 text-slate-900 shadow-md'
                                            : 'bg-white border-slate-200/70 text-slate-700 hover:border-slate-300'
                                    }`}>
                                        <div className="text-emerald-700">{row.ceOi}</div>
                                        <div className={row.ceChg.startsWith('+') ? 'text-emerald-600' : 'text-rose-600'}>{row.ceChg}</div>
                                        <div className="font-extrabold text-sm text-slate-900">{row.strike} {row.isAtm && <span className="text-[9px] bg-[#005451] text-white px-1.5 py-0.5 rounded ml-1">ATM</span>}</div>
                                        <div className={row.peChg.startsWith('+') ? 'text-emerald-600' : 'text-rose-600'}>{row.peChg}</div>
                                        <div className="text-rose-700">{row.peOi}</div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {activeTab === 'signals' && (
                            <div className="grid md:grid-cols-2 gap-6 animate-fade-in">
                                <div className="p-6 rounded-2xl bg-emerald-50/80 border border-emerald-200 space-y-4">
                                    <div className="flex items-center justify-between">
                                        <h4 className="font-black text-emerald-800 text-sm uppercase tracking-wider">CE Side Entered Strikes</h4>
                                        <span className="px-2.5 py-1 rounded-md bg-emerald-600 text-white font-mono text-xs font-bold">3 NEW</span>
                                    </div>
                                    <div className="flex flex-wrap gap-3">
                                        {['24,400', '24,450', '24,500'].map((s, idx) => (
                                            <div key={idx} className="px-4 py-2.5 rounded-xl bg-white border border-emerald-300 font-mono font-bold text-sm text-emerald-800 flex items-center gap-2 shadow-sm">
                                                <span>{s}</span>
                                                <span className="text-[10px] bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded border border-emerald-300">T{idx + 1}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <div className="p-6 rounded-2xl bg-rose-50/80 border border-rose-200 space-y-4">
                                    <div className="flex items-center justify-between">
                                        <h4 className="font-black text-rose-800 text-sm uppercase tracking-wider">PE Side Dropped Strikes</h4>
                                        <span className="px-2.5 py-1 rounded-md bg-rose-600 text-white font-mono text-xs font-bold">2 DROPPED</span>
                                    </div>
                                    <div className="flex flex-wrap gap-3">
                                        {['24,600', '24,650'].map((s, idx) => (
                                            <div key={idx} className="px-4 py-2.5 rounded-xl bg-white border border-rose-300 font-mono font-bold text-sm text-rose-800 flex items-center gap-2 shadow-sm">
                                                <span>{s}</span>
                                                <span className="text-[10px] bg-rose-100 text-rose-800 px-1.5 py-0.5 rounded border border-rose-300">MISSING</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeTab === 'crossover' && (
                            <div className="space-y-4 animate-fade-in">
                                <div className="p-6 rounded-2xl bg-indigo-50/80 border border-indigo-200 flex flex-col md:flex-row items-center justify-between gap-6">
                                    <div className="space-y-1 text-center md:text-left">
                                        <span className="text-xs font-extrabold uppercase text-indigo-700 tracking-wider">Market Regime</span>
                                        <h4 className="text-2xl font-black text-slate-900">Bullish Crossover Triggered</h4>
                                        <p className="text-xs text-slate-600">Call OI momentum exceeded Put OI resistance across top 3 strikes.</p>
                                    </div>
                                    <div className="flex items-center gap-4">
                                        <div className="text-center px-4 py-2.5 rounded-xl bg-white border border-indigo-200 shadow-sm">
                                            <div className="text-[10px] font-bold text-slate-500 uppercase">Variance %</div>
                                            <div className="text-lg font-black text-emerald-600">+34.8%</div>
                                        </div>
                                        <div className="text-center px-4 py-2.5 rounded-xl bg-white border border-indigo-200 shadow-sm">
                                            <div className="text-[10px] font-bold text-slate-500 uppercase">Confidence</div>
                                            <div className="text-lg font-black text-indigo-700">HIGH</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeTab === 'ai' && (
                            <div className="p-6 rounded-2xl bg-slate-50 border border-slate-200 space-y-4 animate-fade-in">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <Cpu className="text-[#005451]" size={20} />
                                        <h4 className="font-black text-slate-900 text-sm uppercase tracking-wider">AI Trade Recommendation Engine</h4>
                                    </div>
                                    <span className="px-2.5 py-1 rounded-md bg-[#005451] text-white font-mono text-xs font-bold shadow-sm">QUANT AI v2.0</span>
                                </div>
                                <div className="p-4 rounded-xl bg-white border border-slate-200 text-xs font-mono space-y-2 text-slate-800 shadow-xs">
                                    <p className="text-emerald-700 font-bold">▶ STRATEGY: Bull Call Spread (Target +180pts)</p>
                                    <p>• Resistance Shift: 24,600 PE Unwinding (-14.8% OI)</p>
                                    <p>• Support Alignment: 24,400 CE Accumulation (+32.4% OI)</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </section>

            {/* --- Architectural Workflow --- */}
            <section className="py-20 px-6 relative z-10 bg-slate-100/60 border-t border-slate-200">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16 space-y-3">
                        <h2 className="text-3xl sm:text-5xl font-black text-slate-900 tracking-tight">How It Works</h2>
                        <p className="text-slate-600 text-base font-medium max-w-md mx-auto">3-Step high velocity quantitative pipeline</p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8">
                        {[
                            { step: '01', title: 'Real-Time Data Feed', desc: 'High-velocity strategy chain data streams processed continuously.', icon: Terminal, color: 'text-[#005451]' },
                            { step: '02', title: 'Dynamic ATM Alignment', desc: 'Auto rounding gap algorithms recalculate strike ranks instantly.', icon: CpuIcon, color: 'text-emerald-600' },
                            { step: '03', title: 'Intraday History Sync', desc: 'Continuous 1-minute historical snapshots stored for instant strategy backtests.', icon: Database, color: 'text-indigo-600' },
                        ].map((item, idx) => (
                            <div key={idx} className="relative p-8 rounded-3xl bg-white border border-slate-200/80 space-y-4 hover:border-[#005451]/40 shadow-lg shadow-slate-200/40 transition-all duration-300">
                                <div className="flex items-center justify-between">
                                    <item.icon className={item.color} size={28} />
                                    <span className="text-3xl font-black font-mono opacity-20 text-slate-900">{item.step}</span>
                                </div>
                                <h3 className="text-xl font-black text-slate-900">{item.title}</h3>
                                <p className="text-sm text-slate-600 font-medium leading-relaxed">{item.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* --- Bento Grid Features --- */}
            <section
                id="features"
                className="py-24 px-6 relative transition-colors duration-500 border-t border-slate-200"
            >
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-20 max-w-2xl mx-auto">
                        <h2 className="text-4xl md:text-5xl font-black text-slate-900 mb-6 tracking-tight">
                            Engineered for <br />
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#005451] via-[#007a76] to-[#00a8a1]">
                                Precision Traders
                            </span>
                        </h2>
                        <p className="text-slate-600 text-lg font-medium">
                            We've condensed complex options greeks and institutional positioning into a unified, high-speed terminal.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-6 auto-rows-[260px]">
                        {/* Large Bento Card 1 */}
                        <div className="md:col-span-2 row-span-2 group relative bg-white border border-slate-200/90 rounded-3xl p-8 md:p-10 overflow-hidden hover:border-[#005451]/50 shadow-xl shadow-slate-200/40 transition-all duration-500">
                            <div className="absolute inset-0 bg-gradient-to-b from-[#005451]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                            <div className="relative z-10 h-full flex flex-col justify-between">
                                <div>
                                    <div className="w-14 h-14 bg-slate-50 rounded-2xl flex items-center justify-center text-[#005451] mb-6 shadow-sm border border-slate-200 group-hover:scale-110 transition-transform duration-500">
                                        <BarChart3 size={28} strokeWidth={2.2} />
                                    </div>
                                    <h3 className="text-3xl font-black text-slate-900 mb-3">
                                        Real-Time OI Decoding
                                    </h3>
                                    <p className="text-slate-600 max-w-md text-lg font-medium leading-relaxed">
                                        Visualize Open Interest buildup and unwinding live. Identify big player positioning instantly with our proprietary strike heatmaps.
                                    </p>
                                </div>
                                {/* Professional Institutional OI Histogram Chart */}
                                <div className="p-4 rounded-2xl bg-slate-50/90 border border-slate-200/80 space-y-3 shadow-xs">
                                    <div className="flex items-center justify-between text-xs font-mono">
                                        <div className="flex items-center gap-4">
                                            <span className="flex items-center gap-1.5 font-bold text-emerald-700">
                                                <span className="w-2.5 h-2.5 rounded-sm bg-emerald-500" /> CE Call OI
                                            </span>
                                            <span className="flex items-center gap-1.5 font-bold text-rose-700">
                                                <span className="w-2.5 h-2.5 rounded-sm bg-rose-500" /> PE Put OI
                                            </span>
                                        </div>
                                        <span className="px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 font-extrabold text-[10px] border border-emerald-300">
                                            ATM: 24,500
                                        </span>
                                    </div>

                                    {/* Histogram Bars */}
                                    <div className="h-32 flex items-end gap-3 pt-2 pb-1 px-1 border-b border-slate-200 relative">
                                        {/* Background Gridlines */}
                                        <div className="absolute inset-0 flex flex-col justify-between pointer-events-none opacity-40">
                                            <div className="border-b border-dashed border-slate-300 w-full" />
                                            <div className="border-b border-dashed border-slate-300 w-full" />
                                            <div className="border-b border-dashed border-slate-300 w-full" />
                                        </div>

                                        {[
                                            { strike: '24,300', ce: 35, pe: 75 },
                                            { strike: '24,400', ce: 50, pe: 90 },
                                            { strike: '24,500', ce: 95, pe: 85, isAtm: true },
                                            { strike: '24,600', ce: 80, pe: 45 },
                                            { strike: '24,700', ce: 60, pe: 25 },
                                        ].map((item, idx) => (
                                            <div key={idx} className="flex-1 h-full flex flex-col justify-end items-center relative z-10 group/bar">
                                                <div className="w-full flex items-end justify-center gap-1 h-full">
                                                    {/* CE Bar */}
                                                    <div 
                                                        className="w-3.5 max-w-[14px] bg-gradient-to-t from-emerald-600 to-emerald-400 rounded-t-sm transition-all duration-500 group-hover/bar:scale-x-110 shadow-xs"
                                                        style={{ height: `${item.ce}%` }}
                                                    />
                                                    {/* PE Bar */}
                                                    <div 
                                                        className="w-3.5 max-w-[14px] bg-gradient-to-t from-rose-600 to-rose-400 rounded-t-sm transition-all duration-500 group-hover/bar:scale-x-110 shadow-xs"
                                                        style={{ height: `${item.pe}%` }}
                                                    />
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Strike Labels */}
                                    <div className="flex justify-between text-[11px] font-mono font-bold text-slate-600 px-1">
                                        <span>24,300</span>
                                        <span>24,400</span>
                                        <span className="text-[#005451] font-black">24,500 (ATM)</span>
                                        <span>24,600</span>
                                        <span>24,700</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Tall Bento Card 2 */}
                        <div className="md:col-span-1 row-span-2 group relative bg-white border border-slate-200/90 rounded-3xl p-8 md:p-10 overflow-hidden hover:border-emerald-500/50 shadow-xl shadow-slate-200/40 transition-all duration-500">
                            <div className="absolute inset-0 bg-gradient-to-b from-emerald-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                            <div className="relative z-10 h-full flex flex-col justify-between">
                                <div>
                                    <div className="w-14 h-14 bg-slate-50 rounded-2xl flex items-center justify-center text-emerald-600 mb-6 shadow-sm border border-slate-200 group-hover:scale-110 transition-transform duration-500">
                                        <Cpu size={28} strokeWidth={2.2} />
                                    </div>
                                    <h3 className="text-2xl font-black text-slate-900 mb-3">AI Strategy Engine</h3>
                                    <p className="text-slate-600 text-base font-medium">
                                        Automated momentum engines scan options volume surfaces to isolate high-probability institutional trades.
                                    </p>
                                </div>

                                <div className="space-y-3 pt-6">
                                    <div className="bg-slate-50 p-4 rounded-2xl border border-emerald-200 flex items-center justify-between shadow-xs">
                                        <div className="flex items-center gap-3">
                                            <div className="w-9 h-9 rounded-xl bg-emerald-100 flex items-center justify-center">
                                                <TrendingUp className="text-emerald-700" size={18} />
                                            </div>
                                            <div>
                                                <div className="text-sm font-black text-slate-900">Bullish Breakout</div>
                                                <div className="text-xs text-slate-500">92% Probability</div>
                                            </div>
                                        </div>
                                        <div className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse shadow-[0_0_10px_rgba(16,185,129,0.8)]" />
                                    </div>
                                    <div className="bg-slate-50 p-4 rounded-2xl border border-rose-200 flex items-center justify-between shadow-xs">
                                        <div className="flex items-center gap-3">
                                            <div className="w-9 h-9 rounded-xl bg-rose-100 flex items-center justify-center">
                                                <Activity className="text-rose-700" size={18} />
                                            </div>
                                            <div>
                                                <div className="text-sm font-black text-slate-900">Vol Spike Alert</div>
                                                <div className="text-xs text-slate-500">High IV Rank</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Standard Bento Card 3 */}
                        <div className="group relative bg-white border border-slate-200/90 rounded-3xl p-8 overflow-hidden hover:border-rose-500/50 shadow-xl shadow-slate-200/40 transition-all duration-500">
                            <div className="relative z-10">
                                <div className="w-14 h-14 bg-slate-50 rounded-2xl flex items-center justify-center text-rose-600 mb-5 shadow-sm border border-slate-200 group-hover:scale-110 transition-transform duration-500">
                                    <Lock size={26} strokeWidth={2.2} />
                                </div>
                                <h3 className="text-xl font-black text-slate-900 mb-2">Auto Support & Resistance</h3>
                                <p className="text-slate-600 text-sm font-medium">
                                    Institutional key levels are calculated dynamically every minute.
                                </p>
                            </div>
                        </div>

                        {/* Standard Bento Card 4 */}
                        <div className="group relative bg-white border border-slate-200/90 rounded-3xl p-8 overflow-hidden hover:border-amber-500/50 shadow-xl shadow-slate-200/40 transition-all duration-500">
                            <div className="relative z-10">
                                <div className="w-14 h-14 bg-slate-50 rounded-2xl flex items-center justify-center text-amber-600 mb-5 shadow-sm border border-slate-200 group-hover:rotate-12 transition-transform duration-500">
                                    <TrendingUp size={26} strokeWidth={2.2} />
                                </div>
                                <h3 className="text-xl font-black text-slate-900 mb-2">Automated Shift Signals</h3>
                                <p className="text-slate-600 text-sm font-medium">
                                    Instant alerts when Call/Put OI resistance shifts dynamically.
                                </p>
                            </div>
                        </div>

                        {/* Wide Bento Card 5 */}
                        <div className="md:col-span-1 group relative rounded-3xl p-1 overflow-hidden transition-all duration-500 shadow-xl">
                            <div className="relative h-full bg-gradient-to-br from-[#005451] via-[#006e6a] to-[#003d3b] rounded-[calc(1.5rem-4px)] p-8 flex flex-col items-center justify-center text-white overflow-hidden shadow-lg shadow-[#005451]/30">
                                <div className="relative z-10 flex flex-col items-center">
                                    <div className="w-14 h-14 bg-white/15 rounded-2xl flex items-center justify-center text-teal-200 mb-4 backdrop-blur-md border border-white/20">
                                        <Shield size={28} strokeWidth={2.5} />
                                    </div>
                                    <h3 className="text-5xl font-black text-white tracking-tighter drop-shadow-sm">
                                        99.9%
                                    </h3>
                                    <p className="text-teal-100 text-xs font-extrabold uppercase tracking-widest mt-1">Uptime Reliability</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* --- Comparison Matrix Section --- */}
            <section className="py-24 px-6 relative z-10 bg-slate-100/50 border-t border-slate-200">
                <div className="max-w-5xl mx-auto">
                    <div className="text-center mb-16 space-y-4">
                        <h2 className="text-3xl sm:text-5xl font-black text-slate-900 tracking-tight">
                            Standard Platforms vs <span className="text-[#005451]">Advanced Quant Terminal</span>
                        </h2>
                        <p className="text-slate-600 text-base font-medium max-w-xl mx-auto">
                            See why modern options quants choose our real-time algorithmic suite.
                        </p>
                    </div>

                    <div className="rounded-3xl border border-slate-200/90 bg-white shadow-2xl shadow-slate-200/60 overflow-hidden">
                        <div className="grid grid-cols-3 p-6 border-b border-slate-200 text-xs font-black uppercase tracking-wider text-slate-500 bg-slate-50">
                            <div>Feature Capability</div>
                            <div className="text-center text-slate-500">Standard Platforms</div>
                            <div className="text-center text-[#005451]">Advanced Quant Terminal</div>
                        </div>

                        {[
                            { feature: 'Real-Time Strategy Chain Engine', legacy: false, oracle: true },
                            { feature: 'Automatic ATM Dynamic Strike Gap', legacy: false, oracle: true },
                            { feature: 'Entered vs Missing Strike Radar', legacy: false, oracle: true },
                            { feature: '1-Minute Intraday Backup Sync', legacy: false, oracle: true },
                            { feature: 'AI Trade Idea Generator Engine', legacy: false, oracle: true },
                        ].map((row, idx) => (
                            <div key={idx} className="grid grid-cols-3 p-5 items-center border-b border-slate-100 text-sm font-medium">
                                <div className="font-bold text-slate-900">{row.feature}</div>
                                <div className="flex justify-center text-rose-500">
                                    <XCircle size={20} />
                                </div>
                                <div className="flex justify-center text-emerald-600">
                                    <CheckCircle2 size={22} className="fill-emerald-500/20" />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* --- Trader Reviews Section --- */}
            <section className="py-20 px-6 relative z-10 bg-gradient-to-b from-white via-slate-50 to-slate-100/50 border-t border-slate-200">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16 space-y-3">
                        <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-emerald-100/80 text-emerald-800 text-xs font-extrabold uppercase tracking-wider border border-emerald-200">
                            <Quote size={14} /> Institutional Feedback
                        </div>
                        <h2 className="text-3xl sm:text-5xl font-black text-slate-900 tracking-tight">Built for Quantitative Execution</h2>
                        <p className="text-slate-600 text-base font-medium max-w-lg mx-auto">High velocity decision tools engineered for active derivative traders.</p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8">
                        {[
                            { quote: "The real-time strike entry & drop tracking is unmatched. I can instantly spot institutional positioning shifts before price breakouts.", author: "Algorithmic Derivatives Trader", role: "NIFTY & SENSEX Specialist" },
                            { quote: "Having dynamic ATM strike alignment with zero data latency completely changed how we execute option strategies on volatile expiry days.", author: "Quantitative Research Analyst", role: "Options Desk Lead" },
                            { quote: "Sub-2 second feeds, 1-minute intraday cloud sync, and zero clutter layout make this terminal our daily primary workstation.", author: "Active Portfolio Manager", role: "Index Volatility Trader" },
                        ].map((card, idx) => (
                            <div key={idx} className="p-8 rounded-3xl bg-white border border-slate-200/90 shadow-xl shadow-slate-200/50 flex flex-col justify-between space-y-6 hover:border-[#005451]/40 transition-all">
                                <p className="text-slate-700 font-medium text-base leading-relaxed italic">"{card.quote}"</p>
                                <div className="pt-4 border-t border-slate-100">
                                    <div className="font-black text-slate-900 text-sm">{card.author}</div>
                                    <div className="text-xs font-bold text-[#005451]">{card.role}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* --- Trader FAQ Section --- */}
            <section className="py-24 px-6 relative z-10 border-t border-slate-200">
                <div className="max-w-4xl mx-auto">
                    <div className="text-center mb-16 space-y-3">
                        <h2 className="text-3xl sm:text-5xl font-black text-slate-900 tracking-tight">Frequently Asked Questions</h2>
                        <p className="text-slate-600 text-base font-medium">Everything you need to know about the quantitative terminal.</p>
                    </div>

                    <div className="space-y-4">
                        {[
                            { q: 'How fast is the options data feed?', a: 'Our terminal connects directly to live data streams, refreshing strategy chains continuously every 2 seconds.' },
                            { q: 'Does it support intraday historical backtests?', a: 'Yes. All 1-minute intraday snapshots are automatically backed up to high-speed cloud database servers for instant historical analysis.' },
                            { q: 'How are Support & Resistance strikes calculated?', a: 'Top 3 Open Interest and Volume concentrations across Call and Put sides are dynamically calculated around the current ATM gap.' },
                            { q: 'How does the AI Trade Generator work?', a: 'The AI engine analyzes delta crossovers, volume squeeze velocity, and missing strike entry patterns to generate high-probability trade ideas.' },
                        ].map((faq, idx) => (
                            <div key={idx} className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden transition-all">
                                <button
                                    onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                                    className="w-full p-6 text-left font-bold text-slate-900 flex items-center justify-between gap-4"
                                >
                                    <span>{faq.q}</span>
                                    <ChevronDown className={`transition-transform duration-300 text-[#005451] ${openFaq === idx ? 'rotate-180' : ''}`} size={20} />
                                </button>
                                {openFaq === idx && (
                                    <div className="px-6 pb-6 text-sm text-slate-600 font-medium leading-relaxed border-t border-slate-100 pt-4">
                                        {faq.a}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* --- Final CTA Section --- */}
            <section
                id="analytics"
                className="py-32 px-6 relative overflow-hidden bg-gradient-to-br from-[#005451] via-[#004240] to-[#002f2d] text-white transition-colors duration-500 min-h-[420px] shadow-2xl"
            >
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[450px] bg-white/10 blur-[140px] pointer-events-none" />

                <div className="relative max-w-4xl mx-auto text-center space-y-8">
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/15 border border-white/20 text-teal-100 text-xs font-bold uppercase tracking-widest backdrop-blur-md">
                        <Sparkles size={14} /> Ready for Institutional Performance?
                    </div>

                    <h2 className="text-4xl sm:text-6xl font-black tracking-tight leading-tight">
                        Experience the Market <br /> with Algorithmic Clarity
                    </h2>

                    <p className="text-xl text-teal-100 max-w-2xl mx-auto font-medium">
                        Join disciplined traders analyzing live options positioning, gamma shifts, and institutional support/resistance.
                    </p>

                    <div className="pt-4">
                        <button
                            onClick={handleAuthAction}
                            className="group relative inline-flex items-center justify-center px-10 py-5 text-xl font-bold text-slate-900 bg-white hover:bg-slate-100 rounded-2xl focus:outline-none shadow-2xl shadow-black/20 hover:-translate-y-1 transition-all duration-300"
                        >
                            <span>Access Options Terminal</span>
                            <ArrowRight className="ml-3 group-hover:translate-x-2 transition-transform text-[#005451]" size={22} />
                        </button>
                    </div>
                </div>
            </section>

            {/* --- Footer --- */}
            <footer
                id="about"
                className="border-t border-slate-200 bg-white py-12 px-6 transition-colors duration-500"
            >
                <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-6">
                    <div className="flex items-center gap-3">
                        <div className="relative w-10 h-10 flex items-center justify-center bg-[#005451] rounded-xl shadow-lg shadow-[#005451]/20">
                            <Activity className="text-white" size={20} strokeWidth={2.5} />
                        </div>
                        <span className="text-slate-900 font-bold tracking-tight">Options Oracle <span className="text-slate-500 font-normal ml-1">© 2026</span></span>
                    </div>
                    <div className="flex gap-8 text-slate-600 font-medium text-sm">
                        <a href="/" className="hover:text-[#005451] transition-colors">Privacy Policy</a>
                        <a href="/" className="hover:text-[#005451] transition-colors">Terms of Service</a>
                        <a href="/" className="hover:text-[#005451] transition-colors">Contact Support</a>
                    </div>
                </div>
            </footer>
        </div>
    )
}
